import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Text, useColorModeValue, useToken } from '@bonusly/design-system';

import type { BizyMatrixNode } from '../../../schemas/bizy-conversations';
import { HighchartsExportBar } from './highcharts-export-bar';
// Importing highcharts-shared registers the heatmap + export modules (side effect).
import { resolveCssColor } from './highcharts-shared';
import { TruncationCaption } from './truncation-caption';

type MatrixPayload = BizyMatrixNode['payload'];
type ValueFormat = MatrixPayload['valueFormat'];

// Heat gradient as literal hex (not theme tokens): the colorAxis interpolates
// between these two, and CSS-var colors can't be interpolated. The MIN end is
// mode-aware — a near-white tint in light mode, a dark navy in dark mode — so
// low-value cells blend with the surface instead of glaring white on a dark
// panel. The MAX end is a medium blue that reads on both backgrounds (and keeps
// the in-cell value labels legible).
const HEAT_MIN_LIGHT = '#eaf2fd';
const HEAT_MIN_DARK = '#1f2d44';
const HEAT_MAX = '#5b8def';

interface BizyMatrixProps {
	title?: string;
	rowLabel?: string;
	columnLabel?: string;
	/** Name of the metric each cell holds, e.g. "Average quality score". */
	valueLabel?: string;
	valueFormat: ValueFormat;
	/** When true, an absent (row, column) is a true zero and renders 0 (a
	 *  complete count grid); otherwise a missing cell is left blank. */
	zeroFill?: boolean;
	rows: string[];
	columns: string[];
	cells: MatrixPayload['cells'];
	/** Drill: clicking a cell sends a scoped follow-up for "row × column". */
	onDrill?: (label: string) => void;
	/** Full row-axis count when rows were trimmed to a subset; drives the
	 *  "N of M" caption. Absent/equal to shown rows → no caption. */
	totalRows?: number;
	/** Ranking direction for the "N of M" caption (top/bottom); absent = neutral. */
	selection?: 'top' | 'bottom';
	/** Expand: re-request the full set when the rows were trimmed. */
	onExpand?: () => void;
}

export function formatValue(value: number, format: ValueFormat): string {
	switch (format) {
		case 'percent':
			return `${value}%`;
		case 'currency':
			return `$${value.toLocaleString()}`;
		default:
			return value.toLocaleString();
	}
}

// Key a (row, column) pair with a NUL delimiter that can't appear in a label,
// so "A B"×"C" and "A"×"B C" don't collide into the same key.
const cellKey = (row: string, col: string) => `${row} ${col}`;

interface BuildHeatmapArgs {
	rowKeys: string[];
	colKeys: string[];
	valueAt: (row: string, col: string) => number | undefined;
	fillZeros: boolean;
	valueFormat: ValueFormat;
	rowLabel?: string;
	columnLabel?: string;
	/** Name of the metric each cell holds; the CSV value-column + tooltip header. */
	valueLabel?: string;
	/** i18n fallbacks for the CSV column headers when the agent omits a label.
	 *  English defaults keep this builder unit-testable on its own. */
	fallbackLabels?: { row: string; column: string; value: string };
	axisColor: string;
	gridColor: string;
	labelColor: string;
	/** Heat gradient stops (mode-aware). Default to the light-mode values so the
	 *  pure options builder stays usable without a color-mode context. */
	minColor?: string;
	maxColor?: string;
	/** When set, cells are clickable and call this with "row × column". */
	onCellClick?: (label: string) => void;
}

// Pure options builder (no DOM) so the heatmap config is unit-testable. Cells map
// to [colIndex, rowIndex, value]; an absent cell is 0 for count grids (fillZeros)
// or null (a blank/N-A cell) otherwise. The color scale is capped to a light tint
// so the dark in-cell value labels stay legible at the densest end.
export function buildHeatmapOptions({
	rowKeys,
	colKeys,
	valueAt,
	fillZeros,
	valueFormat,
	rowLabel,
	columnLabel,
	valueLabel,
	fallbackLabels = { row: 'Row', column: 'Column', value: 'Value' },
	axisColor,
	gridColor,
	labelColor,
	minColor = HEAT_MIN_LIGHT,
	maxColor = HEAT_MAX,
	onCellClick,
}: BuildHeatmapArgs): Highcharts.Options {
	const data: Highcharts.PointOptionsObject[] = [];
	rowKeys.forEach((row, y) =>
		colKeys.forEach((col, x) => {
			const raw = valueAt(row, col);
			data.push({ x, y, value: raw ?? (fillZeros ? 0 : null) });
		})
	);
	const maxValue = Math.max(0, ...data.map((d) => d.value ?? 0)) || 1;
	const axisLabelStyle = { color: axisColor, fontSize: '11px' };

	return {
		chart: { type: 'heatmap', backgroundColor: 'transparent', spacing: [8, 8, 8, 8] },
		title: { text: '' },
		credits: { enabled: false },
		// The heatmap is one unnamed series, so the default CSV headers are
		// "<columnLabel>", "Series 1 (y)", "Series 1 (value)". Name the row + value
		// columns by axis/metric (the x-category column keeps its columnLabel title).
		exporting: {
			enabled: false,
			csv: {
				columnHeaderFormatter(item: { coll?: string } | null, key?: string) {
					if (!item || item.coll === 'xAxis') return columnLabel ?? fallbackLabels.column;
					if (key === 'y') return rowLabel ?? fallbackLabels.row;
					if (key === 'value') return valueLabel ?? fallbackLabels.value;
					return false;
				},
			},
		},
		legend: { enabled: false },
		colorAxis: { min: 0, max: maxValue, minColor, maxColor },
		// useHTML axis labels for the same reason as the bar chart: the side panel
		// can't reliably measure SVG label widths and clips them.
		xAxis: {
			categories: colKeys,
			opposite: true,
			lineColor: gridColor,
			tickColor: gridColor,
			title: columnLabel ? { text: columnLabel, style: axisLabelStyle } : { text: '' },
			labels: { useHTML: true, style: axisLabelStyle },
		},
		yAxis: {
			categories: rowKeys,
			reversed: true,
			title: rowLabel ? { text: rowLabel, style: axisLabelStyle } : { text: '' },
			labels: { useHTML: true, style: axisLabelStyle },
		},
		tooltip: {
			// Inside tooltips are SVG groups that render behind the HTML axis-label
			// overlay, so render OUTSIDE (a body-level div) to escape that stacking
			// — with a high z-index, since the default outside z-index sits behind
			// the side panel.
			useHTML: true,
			outside: true,
			style: { zIndex: 9999 },
			formatter: function (this: { point: Highcharts.Point & { value?: number | null } }) {
				const { x = 0, y = 0, value } = this.point;
				const cell = value == null ? '—' : formatValue(value, valueFormat);
				return `<b>${rowKeys[y]} · ${colKeys[x]}</b><br/>${valueLabel ? `${valueLabel}: ` : ''}${cell}`;
			},
		},
		series: [
			{
				type: 'heatmap',
				borderWidth: 1,
				borderColor: gridColor,
				nullColor: 'transparent',
				data,
				// Drill: clicking a cell sends a scoped follow-up for "row × column".
				...(onCellClick
					? {
							cursor: 'pointer',
							point: {
								events: {
									click: function (this: Highcharts.Point & { x?: number; y?: number }) {
										onCellClick(`${rowKeys[this.y ?? 0]} × ${colKeys[this.x ?? 0]}`);
									},
								},
							},
						}
					: {}),
				dataLabels: {
					enabled: true,
					color: labelColor,
					style: { fontWeight: 'normal', fontSize: '11px', textOutline: 'none' },
					formatter: function (this: { point: { value?: number | null } }) {
						return this.point.value == null ? '' : formatValue(this.point.value, valueFormat);
					},
				},
			},
		],
	};
}

/**
 * Renders the Data Agent's Matrix node — a 2-D categorical grid (department ×
 * program, theme × month, dept ↔ dept) — as a Highcharts heatmap. Cell shade
 * scales with value magnitude (capped so the dark in-cell labels stay legible);
 * absent cells render as 0 for count grids or blank otherwise. A hover-revealed
 * action bar exports the heatmap as CSV or PNG (shared with the chart).
 */
export function BizyMatrix({
	title,
	rowLabel,
	columnLabel,
	valueLabel,
	valueFormat,
	zeroFill,
	rows,
	columns,
	cells,
	onDrill,
	totalRows,
	selection,
	onExpand,
}: BizyMatrixProps) {
	const chartRef = useRef<HighchartsReact.RefObject>(null);
	const { t } = useTranslation('admin_bizy');
	const [axisColor, gridColor, labelColor] = useToken('colors', ['text.muted', 'border.soft', 'text.default']).map(resolveCssColor);
	// Low-value cells blend with the surface: near-white in light mode, dark navy
	// in dark mode (instead of glaring near-white on a dark panel).
	const heatMinColor = useColorModeValue(HEAT_MIN_LIGHT, HEAT_MIN_DARK);

	// De-duplicate axis labels (keep first seen): they double as React keys and
	// Map keys, so a repeat would collide and mirror another cell's data.
	const rowKeys = Array.from(new Set(rows.filter((r) => r.trim().length > 0)));
	const colKeys = Array.from(new Set(columns.filter((c) => c.trim().length > 0)));
	if (rowKeys.length === 0 || colKeys.length === 0) return null;

	// Sparse lookup (last cell wins on a genuine duplicate row/column).
	const byCell = new Map<string, number | undefined>();
	cells.forEach((cell) => byCell.set(cellKey(cell.row, cell.column), cell.value));
	const valueAt = (row: string, col: string) => byCell.get(cellKey(row, col));

	// Count matrices (number) are complete grids where an absent combination is a
	// true 0 (flows, dept × program), so blanks render as 0. Rate/currency
	// matrices leave a missing cell blank (N/A, not 0%). The agent can override
	// via zeroFill. This is deterministic — it needs no cooperation from the LLM.
	const fillZeros = zeroFill ?? valueFormat === 'number';

	const options = buildHeatmapOptions({
		rowKeys,
		colKeys,
		valueAt,
		fillZeros,
		valueFormat,
		rowLabel,
		columnLabel,
		valueLabel,
		fallbackLabels: { row: t('chartFallback.row'), column: t('chartFallback.column'), value: t('chartFallback.value') },
		axisColor,
		gridColor,
		labelColor,
		minColor: heatMinColor,
		onCellClick: onDrill,
	});
	// Room per row plus the (top) column-axis labels.
	const height = rowKeys.length * 36 + 80;

	return (
		<Flex direction="column" gap={2} mb={4} data-testid="bizy-matrix" sx={{ '&:hover .bizy-matrix-actions': { opacity: 1 } }}>
			{title ? (
				<Text fontSize="md" fontWeight="semibold" color="text.default">
					{title}
				</Text>
			) : null}
			<Box width="100%" sx={{ '.highcharts-container': { margin: '0 auto' } }}>
				<HighchartsReact ref={chartRef} highcharts={Highcharts} options={{ ...options, chart: { ...options.chart, height } }} />
			</Box>
			{totalRows != null ? <TruncationCaption shown={rowKeys.length} total={totalRows} selection={selection} onExpand={onExpand} /> : null}
			<HighchartsExportBar chartRef={chartRef} title={title} fallback="matrix" variant="heatmap" idPrefix="bizy-matrix" />
		</Flex>
	);
}
